import { getCachedAnalyticsIdentity } from './analytics-identity';
import Constants from 'expo-constants';
import * as Linking from 'expo-linking';
import { Platform } from 'react-native';
import { parseSetCookieHeader, stripSecureCookiePrefix } from 'better-auth/cookies';

type StorageAdapter = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
};

type ExpoAuthPluginOptions = {
  scheme?: string;
  storage: StorageAdapter;
  storagePrefix?: string;
  cookiePrefix?: string | string[];
  disableCache?: boolean;
  webBrowserOptions?: Record<string, unknown>;
};

type AuthStore = {
  notify?: (key: string) => void;
  atoms?: {
    session?: {
      get: () => Record<string, unknown>;
      set: (value: Record<string, unknown>) => void;
    };
  };
};

const SECURE_COOKIE_PREFIX = '__Secure-';
const STORAGE_VALUE_LIMIT = 1800;
const CHUNK_MARKER = '\u0001ba-chunks:';

function safeJSONParse<T>(value: string | null | undefined): T | null {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function normalizeCookieName(name: string) {
  return name.replace(/:/g, '_');
}

export function createStorageAdapter(storage: StorageAdapter): StorageAdapter {
  return {
    getItem(name) {
      const key = normalizeCookieName(name);
      const stored = storage.getItem(key);
      if (stored == null || !stored.startsWith(CHUNK_MARKER)) {
        return stored;
      }

      const count = Number(stored.slice(CHUNK_MARKER.length));
      if (!Number.isInteger(count) || count < 1) {
        return null;
      }

      let value = '';
      for (let index = 0; index < count; index += 1) {
        const chunk = storage.getItem(`${key}.${index}`);
        if (chunk == null) {
          return null;
        }
        value += chunk;
      }
      return value;
    },
    setItem(name, value) {
      const key = normalizeCookieName(name);
      try {
        if (value.length <= STORAGE_VALUE_LIMIT) {
          storage.setItem(key, value);
          return;
        }

        // Clear the prior commit marker before rewriting chunks. An interrupted
        // write then fails closed instead of reading mixed old/new OAuth data.
        storage.setItem(key, '');
        const count = Math.ceil(value.length / STORAGE_VALUE_LIMIT);
        for (let index = 0; index < count; index += 1) {
          const start = index * STORAGE_VALUE_LIMIT;
          storage.setItem(
            `${key}.${index}`,
            value.slice(start, start + STORAGE_VALUE_LIMIT),
          );
        }
        storage.setItem(key, `${CHUNK_MARKER}${count}`);
      } catch (error) {
        console.error(
          `[better-auth/expo] failed to persist "${key}" to storage`,
          error,
        );
      }
    },
  };
}

function getSetCookie(
  header: string,
  prevCookie?: string,
) {
  const parsed = parseSetCookieHeader(header);
  const toSetCookie =
    safeJSONParse<Record<string, { value: string; expires: string | null }>>(
      prevCookie,
    ) ?? {};

  parsed.forEach((cookie, key) => {
    const expiresAt = cookie.expires;
    const maxAge = cookie['max-age'];

    if (maxAge !== undefined && Number(maxAge) <= 0) {
      delete toSetCookie[key];
      return;
    }

    const expires = maxAge
      ? new Date(Date.now() + Number(maxAge) * 1000)
      : expiresAt
        ? new Date(String(expiresAt))
        : null;

    if (expires && expires.getTime() <= Date.now()) {
      delete toSetCookie[key];
      return;
    }

    toSetCookie[key] = {
      value: String(cookie.value),
      expires: expires ? expires.toISOString() : null,
    };
  });

  return JSON.stringify(toSetCookie);
}

function getCookie(cookie: string) {
  const parsed =
    safeJSONParse<Record<string, { value: string; expires: string | null }>>(
      cookie,
    ) ?? {};

  return Object.entries(parsed).reduce((acc, [key, value]) => {
    if (value.expires && new Date(value.expires) < new Date()) {
      return acc;
    }

    return acc ? `${acc}; ${key}=${value.value}` : `${key}=${value.value}`;
  }, '');
}

function hasSessionCookieChanged(prevCookie: string | null, nextCookie: string) {
  if (!prevCookie) {
    return true;
  }

  try {
    const prev = JSON.parse(prevCookie) as Record<string, { value?: string }>;
    const next = JSON.parse(nextCookie) as Record<string, { value?: string }>;
    const sessionKeys = new Set<string>();

    Object.keys(prev).forEach((key) => {
      if (key.includes('session_token') || key.includes('session_data')) {
        sessionKeys.add(key);
      }
    });

    Object.keys(next).forEach((key) => {
      if (key.includes('session_token') || key.includes('session_data')) {
        sessionKeys.add(key);
      }
    });

    for (const key of sessionKeys) {
      if (prev[key]?.value !== next[key]?.value) {
        return true;
      }
    }

    return false;
  } catch {
    return true;
  }
}

function hasBetterAuthCookies(
  setCookieHeader: string,
  cookiePrefix: string | string[],
) {
  const cookies = parseSetCookieHeader(setCookieHeader);
  const cookieSuffixes = ['session_token', 'session_data'];
  const prefixes = Array.isArray(cookiePrefix) ? cookiePrefix : [cookiePrefix];

  for (const name of cookies.keys()) {
    const nameWithoutSecure = stripSecureCookiePrefix(name);

    for (const prefix of prefixes) {
      if (prefix) {
        if (nameWithoutSecure.startsWith(prefix)) {
          return true;
        }
      } else {
        for (const suffix of cookieSuffixes) {
          if (nameWithoutSecure.endsWith(suffix)) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

function getOAuthStateValue(
  cookieJson: string | null,
  cookiePrefix: string | string[],
) {
  if (!cookieJson) {
    return null;
  }

  const parsed = safeJSONParse<Record<string, { value?: string }>>(cookieJson);
  if (!parsed) {
    return null;
  }

  const prefixes = Array.isArray(cookiePrefix) ? cookiePrefix : [cookiePrefix];

  for (const prefix of prefixes) {
    const candidates = [
      `${SECURE_COOKIE_PREFIX}${prefix}.oauth_state`,
      `${prefix}.oauth_state`,
    ];

    for (const name of candidates) {
      const value = parsed[name]?.value;
      if (value) {
        return value;
      }
    }
  }

  return null;
}

function getOrigin(scheme?: string) {
  return Linking.createURL('', { scheme });
}

function getRequestBodyObject(body: unknown) {
  if (!body) {
    return null;
  }

  if (typeof body === 'string') {
    return safeJSONParse<Record<string, unknown>>(body);
  }

  if (typeof body === 'object') {
    return body as Record<string, unknown>;
  }

  return null;
}

function persistRequestBodyObject(requestOptions: any, body: Record<string, unknown>) {
  if (typeof requestOptions.body === 'string') {
    requestOptions.body = JSON.stringify(body);
  }
}

function resolveNativeCallbackURL(value: unknown, scheme?: string) {
  return typeof value === 'string' && value.startsWith('/')
    ? Linking.createURL(value, { scheme })
    : value;
}

function createRequestCanceledError() {
  const error = new Error('Authentication was canceled') as Error & {
    code?: string;
  };
  error.code = 'ERR_REQUEST_CANCELED';
  return error;
}

export function createTabbioExpoAuthPlugin(
  options: ExpoAuthPluginOptions,
): any {
  let store: AuthStore | null = null;
  const isWeb = Platform.OS === 'web';
  const storagePrefix = options.storagePrefix || 'better-auth';
  const cookieName = `${storagePrefix}_cookie`;
  const localCacheName = `${storagePrefix}_session_data`;
  const storage = createStorageAdapter(options.storage);
  const cookiePrefix = options.cookiePrefix || 'better-auth';
  const rawScheme =
    options.scheme ||
    Constants.expoConfig?.scheme ||
    (Constants.platform as { scheme?: string | string[] } | null)?.scheme;
  const scheme = Array.isArray(rawScheme) ? rawScheme[0] : rawScheme;
  let nativeOAuthSessionInFlight = false;

  if (!scheme && !isWeb) {
    throw new Error('Scheme not found in app.json. Please provide a scheme.');
  }

  return {
    id: 'expo',
    getActions(_: unknown, authStore: AuthStore) {
      store = authStore;

      return {
        getCookie: () => getCookie(storage.getItem(cookieName) || '{}'),
      };
    },
    fetchPlugins: [
      {
        id: 'expo',
        name: 'Expo',
        hooks: {
          async onSuccess(context: any) {
            if (isWeb) {
              return;
            }

            const setCookie = context.response.headers.get('set-cookie');

            if (setCookie && hasBetterAuthCookies(setCookie, cookiePrefix)) {
              const previousCookie = storage.getItem(cookieName);
              const nextCookie = getSetCookie(setCookie, previousCookie ?? undefined);

              storage.setItem(cookieName, nextCookie);

              if (hasSessionCookieChanged(previousCookie, nextCookie)) {
                store?.notify?.('$sessionSignal');
              }
            }

            const requestUrl = context.request.url.toString();

            if (requestUrl.includes('/get-session') && !options.disableCache) {
              storage.setItem(localCacheName, JSON.stringify(context.data));
            }

            const isOAuthSignIn =
              context.data?.redirect &&
              (requestUrl.includes('/sign-in') || requestUrl.includes('/link-social')) &&
              !context.request?.body?.includes?.('idToken');

            if (!isOAuthSignIn) {
              return;
            }

            const callbackURL = JSON.parse(context.request.body || '{}')?.callbackURL;
            const signInURL = context.data?.url;

            if (!callbackURL || !signInURL) {
              return;
            }

            const Browser = await import('expo-web-browser');

            if (nativeOAuthSessionInFlight) {
              throw createRequestCanceledError();
            }

            nativeOAuthSessionInFlight = true;

            try {
              // Best-effort cleanup protects against double taps or a stale
              // SFAuthenticationSession left behind by an interrupted handoff.
              try {
                Browser.dismissAuthSession();
              } catch {}

              const oauthStateValue = getOAuthStateValue(
                storage.getItem(cookieName),
                cookiePrefix,
              );
              const params = new URLSearchParams({ authorizationURL: signInURL });

              if (oauthStateValue) {
                params.append('oauthState', oauthStateValue);
              }

              const proxyURL = `${context.request.baseURL}/expo-authorization-proxy?${params.toString()}`;
              const result = await Browser.openAuthSessionAsync(
                proxyURL,
                callbackURL,
                options.webBrowserOptions as never,
              );

              if (result.type !== 'success') {
                throw createRequestCanceledError();
              }

              const cookie = new URL(result.url).searchParams.get('cookie');
              if (!cookie) {
                // The session can also close without a cookie when the user taps
                // "Back to sign-in" on the server-rendered sign-in-error page,
                // which deep-links back via the app scheme. Treat any cookieless
                // close as a cancellation so the user simply returns to the
                // native sign-in screen instead of seeing an error.
                throw createRequestCanceledError();
              }

              const nextCookie = getSetCookie(
                cookie,
                storage.getItem(cookieName) ?? undefined,
              );
              storage.setItem(cookieName, nextCookie);
              store?.notify?.('$sessionSignal');
            } finally {
              nativeOAuthSessionInFlight = false;
            }
          },
        },
        async init(url: string, requestOptions: any = {}) {
          if (isWeb) {
            return {
              url,
              options: requestOptions,
            };
          }

          // Same consented instance that the authenticated sync will present.
          // Cached after Firebase consent, so optional analytics adds no wait to auth.
          const analyticsIdentity = getCachedAnalyticsIdentity();
          requestOptions.headers = {
            ...requestOptions.headers,
            'x-tabbio-client-platform': Platform.OS,
            ...(analyticsIdentity ? { 'x-tabbio-analytics-instance': analyticsIdentity.appInstanceId } : {}),
          };
          if (!analyticsIdentity) delete requestOptions.headers['x-tabbio-analytics-instance'];
          requestOptions.credentials = 'omit';

          const body = getRequestBodyObject(requestOptions.body);

          if (body?.idToken !== undefined) {
            requestOptions.headers = {
              ...requestOptions.headers,
              'x-skip-oauth-proxy': 'true',
            };
          } else {
            const cookie = getCookie(storage.getItem(cookieName) || '{}');

            requestOptions.headers = {
              ...requestOptions.headers,
              ...(cookie ? { cookie } : {}),
              'expo-origin': getOrigin(scheme),
              'x-skip-oauth-proxy': 'true',
            };

            if (body) {
              body.callbackURL = resolveNativeCallbackURL(body.callbackURL, scheme);
              body.newUserCallbackURL = resolveNativeCallbackURL(
                body.newUserCallbackURL,
                scheme,
              );
              body.errorCallbackURL = resolveNativeCallbackURL(
                body.errorCallbackURL,
                scheme,
              );
              persistRequestBodyObject(requestOptions, body);
            }

            if (url.includes('/sign-out')) {
              storage.setItem(cookieName, '{}');
              store?.atoms?.session?.set({
                ...store.atoms.session.get(),
                data: null,
                error: null,
                isPending: false,
              });
              storage.setItem(localCacheName, '{}');
            }
          }

          return {
            url,
            options: requestOptions,
          };
        },
      },
    ],
  };
}
